import { useEffect, useState } from 'react';
import { namespaces, useSharedTranslation } from 'us-react-components/dist/hooks/useSharedTranslation';
import { observer } from 'mobx-react-lite';

import { AccordionItem } from 'us-react-components/dist/AccordionItem/AccordionItem';
import {
  Language,
  LanguageService,
  SpecialService,
  SpecialServicesService,
  SpecialSituation,
  SpecialSituationsService,
  TeachingSkill,
  TeachingSkillsService,
  WillSit,
  WillSitKey,
} from 'us-web-services';
import { CheckboxListItem } from 'us-react-components/dist/CheckboxListItem/CheckboxListItem';
import { useDependencies } from '../../dependencies';

const itemBodyStyle = 'text-sm text-slate-600 mb-4';
const listContainerStyle = 'flex flex-wrap';
const listMapTwoColStyle = 'w-full lg:w-1/2 px-1';
const listMapThreeColStyle = 'w-1/2 lg:w-1/3 px-1';

const HighlightGuide = observer((): JSX.Element => {
  const { stores: { sitter } } = useDependencies();
  const { t } = useSharedTranslation(namespaces.caregiver);

  const [isUpdating, setIsUpdating] = useState(false);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [specialServices, setSpecialServices] = useState<SpecialService[]>([]);
  const [specialSituations, setSpecialSituations] = useState<SpecialSituation[]>([]);
  const [teachingSkills, setTeachingSkills] = useState<TeachingSkill[]>([]);

  const isLanguageSelected = (language: Language): boolean => !!sitter.sitter.languages?.find((l) => l.name === language.name);
  const isServiceSelected = (service: SpecialService): boolean => !!sitter.sitter.specialServices?.find((s) => s.name === service.name);
  const isSituationSelected = (situation: SpecialSituation): boolean => !!sitter.specialSituations?.find((e) => e.specialSituation.name === situation.name);
  const isSkillSelected = (skill: TeachingSkill): boolean => !!sitter.teachingSkills?.find((s) => s.teachingSkill.name === skill.name);
  const isVerticalSelected = (vertical: WillSit): boolean => !!sitter.sitter[`willSit${vertical}`];

  const toggleLanguageSelected = async (language: Language): Promise<void> => {
    setIsUpdating(true);

    try {
      if (isLanguageSelected(language)) {
        await sitter.languageRemove(language);
      } else {
        await sitter.languageAdd(language);
      }
    } catch (e) {
      console.warn(e);
    } finally {
      setIsUpdating(false);
    }
  };

  const toggleServiceSelected = async (service: SpecialService): Promise<void> => {
    setIsUpdating(true);

    try {
      if (isServiceSelected(service)) {
        await sitter.specialServiceRemove(service);
      } else {
        await sitter.specialServiceAdd(service);
      }
    } catch (e) {
      console.warn(e);
    } finally {
      setIsUpdating(false);
    }
  };

  const toggleSituationSelected = async (situation: SpecialSituation): Promise<void> => {
    setIsUpdating(true);

    try {
      if (isSituationSelected(situation)) {
        await sitter.specialSituationRemove(situation);
      } else {
        await sitter.specialSituationAdd(situation);
      }
    } catch (e) {
      console.warn(e);
    } finally {
      setIsUpdating(false);
    }
  };

  const toggleSkillSelected = async (skill: TeachingSkill): Promise<void> => {
    setIsUpdating(true);

    try {
      if (isSkillSelected(skill)) {
        await sitter.teachingSkillRemove(skill);
      } else {
        await sitter.teachingSkillAdd(skill);
      }
    } catch (e) {
      console.warn(e);
    } finally {
      setIsUpdating(false);
    }
  };

  const toggleVerticalSelected = async (vertical: WillSit): Promise<void> => {
    setIsUpdating(true);

    try {
      if (isVerticalSelected(vertical)) {
        await sitter.verticalRemove(vertical);
      } else {
        await sitter.verticalAdd(vertical);
      }
    } catch (e) {
      console.warn(e);
    } finally {
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    const getSpecialSituations = async (): Promise<void> => {
      try {
        await sitter.getSpecialSituations();
        const situationList: SpecialSituation[] = (await SpecialSituationsService.getByFilter()).data.data;
        setSpecialSituations(situationList.filter(situation => situation.name?.trim()));
      } catch (e) {
        console.warn(e);
      }
    };

    const getSpecialServices = async (): Promise<void> => {
      try {
        const serviceList: SpecialService[] = (await SpecialServicesService.getByFilter()).data.data;
        setSpecialServices(serviceList.filter(service => service.name?.trim()));
      } catch (e) {
        console.warn(e);
      }
    };

    const getLanguages = async (): Promise<void> => {
      try {
        const languageList: Language[] = (await LanguageService.getByFilter()).data.data;

        const sortedLanguageList = [...languageList].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

        setLanguages(sortedLanguageList);
      } catch (e) {
        console.warn(e);
      }
    };

    const getTeachingSkills = async (): Promise<void> => {
      try {
        await sitter.getTeachingSkills();
        const skillList: TeachingSkill[] = (await TeachingSkillsService.getByFilter()).data.data;
        setTeachingSkills(skillList.filter(skill => skill.name?.trim()));
      } catch (e) {
        console.warn(e);
      }
    };

    getLanguages();
    getSpecialServices();
    getSpecialSituations();
    getTeachingSkills();
  }, []);

  return (
    <div>
      <h4 className='my-1 font-bold text-base'>{t('components.highlightGuide.heading')}</h4>
      <AccordionItem title={t('components.highlightGuide.moreWays')}>
        <div className={itemBodyStyle}>{t('components.highlightGuide.moreWaysBody')}</div>
        <div className={listContainerStyle}>
          {specialServices.map((service: SpecialService) => (
            <div className={listMapTwoColStyle} key={service.id}>
              <CheckboxListItem
                expandOnDesktop
                name={service.name ?? ''}
                text={service.name ?? ''}
                disabled={isUpdating}
                selected={isServiceSelected(service)}
                toggleSelected={(): Promise<void> => toggleServiceSelected(service)}
              />
            </div>
          ))}
        </div>
      </AccordionItem>
      <AccordionItem title={t('components.highlightGuide.additionalLanguages')}>
        <div className={itemBodyStyle}>{t('components.highlightGuide.additionalLanguagesBody')}</div>
        <div className={listContainerStyle}>
          {languages.map((language: Language) => (
            <div className={`${listMapThreeColStyle}${language.name === 'American Sign Language' ? ' !w-full xl:!w-1/3' : ''}`} key={language.id}>
              <CheckboxListItem
                expandOnDesktop
                name={language.name ?? ''}
                text={language.name ?? ''}
                disabled={isUpdating}
                selected={isLanguageSelected(language)}
                toggleSelected={(): Promise<void> => toggleLanguageSelected(language)}
              />
            </div>
          ))}
        </div>
      </AccordionItem>
      <AccordionItem title={t('components.highlightGuide.letSkillsShine')}>
        <div className={itemBodyStyle}>{t('components.highlightGuide.letSkillsShineBody')}</div>
        <div className={listContainerStyle}>
          {teachingSkills.map((skill: TeachingSkill) => (
            <div className={listMapTwoColStyle} key={skill.id}>
              <CheckboxListItem
                expandOnDesktop
                name={skill.name ?? ''}
                text={skill.name ?? ''}
                disabled={isUpdating}
                selected={isSkillSelected(skill)}
                toggleSelected={(): Promise<void> => toggleSkillSelected(skill)}
              />
            </div>
          ))}
        </div>
      </AccordionItem>
      <AccordionItem title={t('components.highlightGuide.newWays')}>
        <div className={itemBodyStyle}>{t('components.highlightGuide.newWaysBody')}</div>
        <div className={listContainerStyle}>
          {specialSituations.map((situation: SpecialSituation) => (
            <div className={listMapTwoColStyle} key={situation.id}>
              <CheckboxListItem
                expandOnDesktop
                name={situation.name ?? ''}
                text={situation.name ?? ''}
                disabled={isUpdating}
                selected={isSituationSelected(situation)}
                toggleSelected={(): Promise<void> => toggleSituationSelected(situation)}
              />
            </div>
          ))}
        </div>
      </AccordionItem>
      <AccordionItem title={t('components.highlightGuide.moreThanCare')}>
        <div className={itemBodyStyle}>{t('components.highlightGuide.moreThanCareBody')}</div>
        <div className={listContainerStyle}>
          {Object.keys(WillSit).map((verticalKey: string) => {
            const vertical = WillSit[verticalKey as WillSitKey];

            return (
              <div className={listMapTwoColStyle} key={verticalKey}>
                <CheckboxListItem
                  expandOnDesktop
                  name={verticalKey}
                  text={t(`verticalTypes.${verticalKey}`)}
                  disabled={isUpdating}
                  selected={isVerticalSelected(vertical)}
                  toggleSelected={(): Promise<void> => toggleVerticalSelected(vertical)}
                />
              </div>
            );
          })}
        </div>
      </AccordionItem>
    </div>
  );
});

export { HighlightGuide };
